import { Profile } from "@/hooks/profile/types";
import { format } from "date-fns";
import { es } from "date-fns/locale";
import { Cake, Sparkles, UserCog } from "lucide-react";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { ConfettiEffect } from "@/components/routines/workout/components/completion/components/ConfettiEffect";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

interface WelcomeHeaderProps {
  profile: Profile | null;
  gymLogo?: string;
  gymName?: string;
  canActAsRecepcionista?: boolean;
}

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour >= 5 && hour < 12) return "Buenos días";
  if (hour >= 12 && hour < 19) return "Buenas tardes";
  return "Buenas noches";
};

const getInitials = (profile: Profile | null) => {
  if (!profile) return "CL";
  const first = profile.first_name?.charAt(0) || "";
  const last = profile.last_name?.charAt(0) || "";
  return (first + last).toUpperCase() || "CL";
};

export const WelcomeHeader = ({ profile, gymLogo, gymName, canActAsRecepcionista }: WelcomeHeaderProps) => {
  const isAmareloGym = gymName?.toUpperCase().includes("AMARELO");
  const [showConfetti, setShowConfetti] = useState(false);
  const navigate = useNavigate();

  // Check if today is the user's birthday
  const isBirthday = () => {
    if (!profile?.birthdate) return false;
    
    const today = new Date();
    let birthdate = new Date(profile.birthdate);
    
    birthdate.setDate(birthdate.getDate() + 1);

    return (
      today.getDate() === birthdate.getDate() &&
      today.getMonth() === birthdate.getMonth()
    );
  };

  useEffect(() => {
    if (isBirthday() && profile?.id) {
      const confettiShown = sessionStorage.getItem(`birthday-confetti-${profile.id}`);
      if (!confettiShown) {
        setShowConfetti(true);
        sessionStorage.setItem(`birthday-confetti-${profile.id}`, 'true');
      }
    }
  }, [profile?.id]);

  return (
    <header className="pt-16 sm:pt-12 space-y-4">
      {/* Main welcome card with glassmorphism */}
      <div className="glass-card p-5 sm:p-6 animate-fade-in">
        <div className="flex items-center gap-4">
          {/* User Avatar */}
          <div className="relative">
            <Avatar className="h-14 w-14 sm:h-16 sm:w-16 ring-2 ring-primary/30 ring-offset-2 ring-offset-background animate-scale-in">
              <AvatarImage 
                src={profile?.avatar_url || undefined} 
                alt={profile?.first_name || "Usuario"}
                className="object-cover"
              />
              <AvatarFallback className="bg-primary/20 text-primary font-bold text-lg">
                {getInitials(profile)}
              </AvatarFallback>
            </Avatar>
            {/* Online indicator */}
            <span className="absolute bottom-0 right-0 h-3.5 w-3.5 rounded-full bg-green-500 ring-2 ring-background" />
          </div>

          {/* Greeting text */}
          <div className="flex-1 min-w-0">
            <p className="text-sm text-muted-foreground animate-slide-up opacity-0 stagger-1">
              {getGreeting()}
            </p>
            <h1 className="text-xl sm:text-2xl font-heading font-bold text-primary truncate animate-slide-up opacity-0 stagger-2">
              {profile?.first_name || "Clubber"} <span className="wave-animation">👋</span>
            </h1>
            <p className="text-muted-foreground text-sm animate-slide-up opacity-0 stagger-3 flex items-center gap-1.5">
              {profile?.role === "profesor" ? (
                <>
                  <Sparkles className="h-3.5 w-3.5 text-primary" />
                  Panel de instructor
                </>
              ) : (
                format(new Date(), "EEEE, d 'de' MMMM", { locale: es })
              )}
            </p>

            {/* Recepcionista mode access button (next to the name, MASTERGYM profes only) */}
            {canActAsRecepcionista && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => navigate("/recepcionista")}
                className="mt-2 h-8 px-3 gap-1.5 border-primary/40 bg-primary/10 text-primary hover:bg-primary/20 hover:text-primary backdrop-blur-sm animate-fade-in"
              >
                <UserCog className="h-3.5 w-3.5" />
                <span className="text-xs font-medium">Entrar como recepcionista</span>
              </Button>
            )}
          </div>

          {/* Gym Logo */}
          {gymLogo && (
            <div className="animate-scale-in stagger-3">
              <img
                src={isAmareloGym ? "/images/amarelo-gym-home-logo.svg" : gymLogo}
                alt="Logo del gimnasio"
                className={
                  isAmareloGym
                    ? "h-10 w-20 sm:h-14 sm:w-28 object-contain amarelo-logo-invert"
                    : "h-10 w-10 sm:h-14 sm:w-14 object-cover rounded-xl shadow-lg ring-1 ring-primary/10"
                }
              />
            </div>
          )}
        </div>
      </div>
      
      {/* Birthday Alert */}
      {isBirthday() && (
        <>
          {showConfetti && <ConfettiEffect />}
          <Alert className="glass-card border-primary/30 animate-scale-in bg-gradient-to-r from-primary/20 to-accent/20">
            <Cake className="h-5 w-5 text-primary animate-float"/>
            <AlertTitle className="text-primary font-bold">¡Feliz Cumpleaños!</AlertTitle>
            <AlertDescription className="text-muted-foreground">
              ¡El equipo de Clubbers te desea un feliz día!
            </AlertDescription>
          </Alert>
        </>
      )}
    </header>
  );
};
